import _debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { selectUserIsLogged } from 'selectors/user';
import {
  selectCurrentUserIsOwner,
  selectCurrentVideoId,
  selectHasEnded,
  selectPaused,
  selectPlayerTime,
  selectVideoDuration,
} from 'selectors/videoPlayer';
import { updateLastWatchTime } from 'utilities/last-watch';
import { getParam } from 'utilities/url';

// NOTE: Please add ?debug=continueWatching to the video
// page when working on this file.
// The logs can be help debug some unexpected behavior
const debugParam = getParam('debug') || '';
const DEBUG = debugParam.includes('continueWatching');

const WATCHED_THRESHOLD = 5; // seconds

const updateLastWatchTimeWrapper = (videoId, timestamp) => {
  if (DEBUG) {
    console.log('Sending request to update watch time');
  }

  return updateLastWatchTime(videoId, timestamp);
};

const ContinueWatchingObserver = () => {
  const completedWatchingRef = useRef(false);

  const userLoggedIn = useSelector(selectUserIsLogged);
  const videoId = useSelector(selectCurrentVideoId);
  const duration = useSelector(selectVideoDuration);
  const currentTime = useSelector(selectPlayerTime);
  const paused = useSelector(selectPaused);
  const ended = useSelector(selectHasEnded);
  const currentUserIsVideoOwner = useSelector(selectCurrentUserIsOwner);

  const updateLastWatchTimeDebounce = useMemo(() => {
    // For regular viewers the lastWatchTime is updated as part of calls
    // to updateVideoPlaySegment
    if (!currentUserIsVideoOwner) {
      return () => {};
    }

    return _debounce(updateLastWatchTimeWrapper, 1500);
  }, [currentUserIsVideoOwner]);

  // Pause hook
  useEffect(() => {
    if (!userLoggedIn || !paused || !videoId || !currentTime) {
      return;
    }

    const timeLeft = duration - currentTime;

    // We clear the watch time in the 'progress' hook
    // during ht last 5 seconds
    if (timeLeft <= WATCHED_THRESHOLD) {
      return;
    }

    if (DEBUG) {
      console.log('PAUSE hook. Saving watch time', currentTime);
    }

    updateLastWatchTimeDebounce(videoId, currentTime);
  }, [paused, videoId, updateLastWatchTimeDebounce]);

  // Ended hook
  useEffect(() => {
    if (!userLoggedIn || !ended || !videoId || completedWatchingRef.current) {
      return;
    }

    if (DEBUG) {
      console.log('Ended HOOK. Clearing watch time');
    }

    updateLastWatchTimeDebounce(videoId, null);
    completedWatchingRef.current = true;
  }, [ended, updateLastWatchTimeDebounce, videoId]);

  // Progress hook
  useEffect(() => {
    if (
      !userLoggedIn ||
      !duration ||
      !videoId ||
      completedWatchingRef.current
    ) {
      return;
    }

    const timeLeft = duration - currentTime;

    if (timeLeft <= WATCHED_THRESHOLD) {
      if (DEBUG) {
        console.log('Progress HOOK. At end of video. Clearing watch time');
      }

      updateLastWatchTimeDebounce(videoId, null);
      completedWatchingRef.current = true;
    }
  }, [currentTime, duration, updateLastWatchTimeDebounce, videoId]);

  // Reset completedWatching if they skip back and
  // rewatch the video
  useEffect(() => {
    if (!userLoggedIn || paused || !completedWatchingRef.current) {
      return;
    }

    const timeLeft = duration - currentTime;

    if (timeLeft <= WATCHED_THRESHOLD) {
      return;
    }

    if (DEBUG) {
      console.log('Reset completed watching hook');
    }

    completedWatchingRef.current = false;
  }, [paused]);

  return null;
};

export default ContinueWatchingObserver;
